// ============================================================
// usePostureSession.ts — 訓練 Session 管理自定義 Hook
// 封裝 Session 狀態、提交邏輯、進度查詢等通用操作
// ============================================================

import { useState, useCallback, useRef } from 'react';
import {
    PostureSessionPayload,
    AIFeedbackResponse,
    StaffProgress,
} from 'types/posture';
import {
    submitPostureSession,
    getStaffProgress,
    generateReport,
} from 'api/client';

// ── Hook 回傳型別 ─────────────────────────────────────────
interface UsePostureSessionReturn {
    // 狀態
    isSubmitting:   boolean;
    lastFeedback:   AIFeedbackResponse | null;
    progress:       StaffProgress | null;
    error:          string | null;
    sessionHistory: PostureSessionPayload[];

    // 操作
    submitSession:    (payload: PostureSessionPayload) => Promise<void>;
    fetchProgress:    (staffId: number) => Promise<void>;
    downloadReport:   (staffId: number) => Promise<void>;
    clearError:       () => void;
}

/**
 * 姿勢訓練 Session 管理 Hook
 * 封裝所有與後端 Session 交互的業務邏輯
 *
 * @example
 * const { submitSession, lastFeedback, isSubmitting } = usePostureSession();
 */
export function usePostureSession(): UsePostureSessionReturn {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [lastFeedback, setLastFeedback] = useState<AIFeedbackResponse | null>(null);
    const [progress,     setProgress]     = useState<StaffProgress | null>(null);
    const [error,        setError]        = useState<string | null>(null);
    const [sessionHistory, setSessionHistory] = useState<PostureSessionPayload[]>([]);

    // 防止重複提交（同一時間只允許一個提交請求）
    const submittingRef = useRef<boolean>(false);

    /**
     * 提交訓練 Session 至後端
     * 自動處理載入狀態和錯誤
     */
    const submitSession = useCallback(async (payload: PostureSessionPayload): Promise<void> => {
        if (submittingRef.current) return; // 防止重複提交

        submittingRef.current = true;
        setIsSubmitting(true);
        setError(null);

        try {
            const feedback = await submitPostureSession(payload);
            setLastFeedback(feedback);
            // 加入本地歷史記錄
            setSessionHistory(prev => [payload, ...prev].slice(0, 50)); // 最多保留 50 筆
        } catch (err) {
            const message = err instanceof Error ? err.message : '提交失敗，請稍後再試';
            setError(message);
            console.error('[usePostureSession] 提交失敗：', err);
        } finally {
            setIsSubmitting(false);
            submittingRef.current = false;
        }
    }, []);

    /**
     * 獲取員工進度統計
     */
    const fetchProgress = useCallback(async (staffId: number): Promise<void> => {
        try {
            const data = await getStaffProgress(staffId);
            setProgress(data);
        } catch (err) {
            console.error('[usePostureSession] 獲取進度失敗：', err);
        }
    }, []);

    /**
     * 下載 PDF 個人報告
     * 自動觸發瀏覽器下載對話框
     */
    const downloadReport = useCallback(async (staffId: number): Promise<void> => {
        setError(null);
        try {
            // 從後端獲取 HTML Blob
            const blob = await generateReport(staffId);

            // 建立臨時 URL 並觸發下載
            const url      = URL.createObjectURL(blob);
            const anchor   = document.createElement('a');
            anchor.href    = url;
            anchor.download = `posture-report-${staffId}-${new Date().toISOString().split('T')[0]}.html`;
            document.body.appendChild(anchor);
            anchor.click();

            // 清理臨時資源
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
        } catch (err) {
            const message = err instanceof Error ? err.message : '下載報告失敗，請稍後再試';
            setError(message);
            console.error('[usePostureSession] 下載報告失敗：', err);
        }
    }, []);

    /** 清除錯誤訊息 */
    const clearError = useCallback((): void => {
        setError(null);
    }, []);

    return {
        isSubmitting,
        lastFeedback,
        progress,
        error,
        sessionHistory,
        submitSession,
        fetchProgress,
        downloadReport,
        clearError,
    };
}

